import * as React from 'react'
import { ActionButton } from 'uiComponents/buttons'
import { ModalDialog, ModalDialogTitle, NavigationSection } from 'uiComponents/popups/modal'
import { ChartDataLoader } from 'uiComponents/loaders'
import { NumberInput, TextInput, TextArea } from 'uiComponents/input'
import { FormItem, FormItemName, ValidationMessage } from 'uiComponents/form/formElements'
import { DatePicker } from 'uiComponents/popups/datePickerInput'
import { format } from 'date-fns'
import { GenerateReservationCodespayload } from 'venue/bookingCodes/bookingCodesService'
import { useGetAllProductsForSelect, useIsLoadingCategoriesOrArticles } from 'products/redux'
import { parseDate } from 'utils'
import { MultiSelectCombobox } from 'uiComponents/combobox/combobox'
import { useTranslation } from 'translations'

interface GenerateCodesDialogProps {
    accountSlug: string
    loading: boolean
    onCancel: () => void
    onConfirm: (payload: GenerateReservationCodespayload) => void
}

export function GenerateCodesDialog({ accountSlug, loading, onCancel, onConfirm }: GenerateCodesDialogProps) {
    const { t } = useTranslation()
    const [name, setName] = React.useState<string>('')
    const [prefix, setPrefix] = React.useState<string>('')
    const [note, setNote] = React.useState<string>('')
    const [size, setSize] = React.useState<string>('')
    const [validFrom, setValidFrom] = React.useState<string>('')
    const [validTo, setValidTo] = React.useState<string>('')
    const [products, setProducts] = React.useState<string[]>([])
    const [validate, setValidate] = React.useState<boolean>(false)
    const [disabledSave, setDisabledSave] = React.useState<boolean>(false)
    const productsForSelect = useGetAllProductsForSelect()
    const isLoadingCategoriesOrArticles = useIsLoadingCategoriesOrArticles()

    const validName = () => !!name.trim()
    const validSize = () => !!size && Number(size) >= 1 && Number(size) <= 100000
    const validFromDate = () => !!validFrom
    const validToDate = () => !!validTo && validFrom <= validTo
    const validProducts = () => !!products.length
    const formValid = () => {
        return validName() && validSize() && validFromDate() && validToDate() && validProducts()
    }

    const onSave = () => {
        setValidate(true)
        if (!formValid() || disabledSave) {
            return
        }
        setDisabledSave(true)
        const payload = {
            name,
            validFrom,
            validTo,
            prefix: prefix.trim(),
            barcodeScanningNote: note.trim(),
            amount: Number(size),
            articles: products,
        }
        onConfirm(payload)
    }

    return (
        <ModalDialog onDismiss={onCancel} interactive fromTop="10%">
            {loading && <ChartDataLoader />}
            <div style={{ width: '25em' }}>
                <ModalDialogTitle>{t('venue.bookingCodes.reservationCodePools.generateCodes.title')}</ModalDialogTitle>
                <FormItem htmlFor="batch-name">
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.nameLabel')}</FormItemName>
                    <TextInput
                        id="batch-name"
                        type="text"
                        placeholder={t('venue.bookingCodes.reservationCodePools.generateCodes.namePlaceholder')}
                        maxLength={100}
                        value={name}
                        onChange={(e) => setName(e.target.value)}
                        status={validate && !validName() ? 'error' : 'normal'}
                        block
                    />
                    <ValidationMessage className={validate && !validName() ? 'validation-message-visible' : ''}>
                        {t('venue.bookingCodes.reservationCodePools.generateCodes.nameError')}
                    </ValidationMessage>
                </FormItem>
                <FormItem htmlFor="batch-products" style={{ paddingBottom: '0.625em' }}>
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.productsLabel')}</FormItemName>
                    <MultiSelectCombobox
                        id="batch-products"
                        value={products}
                        options={productsForSelect}
                        loading={isLoadingCategoriesOrArticles}
                        onChange={(value: string[]) => setProducts(value)}
                    />
                    <ValidationMessage
                        className={validate && !validProducts() ? 'validation-message-visible' : ''}
                        style={{ top: '0.2em' }}
                    >
                        {t('venue.bookingCodes.reservationCodePools.generateCodes.productsError')}
                    </ValidationMessage>
                </FormItem>
                <FormItem htmlFor="batch-start-date" style={{ paddingBottom: '0.625em' }}>
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.validFromLabel')}</FormItemName>
                    <DatePicker
                        id="batch-start-date"
                        date={validFrom ? parseDate(validFrom) : null}
                        onChange={(value: Date) => setValidFrom(value ? format(value, 'yyyy-MM-dd') : '')}
                        status={validate && !validFromDate() ? 'error' : 'normal'}
                    />
                    <ValidationMessage
                        className={validate && !validFromDate() ? 'validation-message-visible' : ''}
                        style={{ top: '0.2em' }}
                    >
                        {t('venue.bookingCodes.reservationCodePools.generateCodes.validFromError')}
                    </ValidationMessage>
                </FormItem>
                <FormItem htmlFor="batch-end-date" style={{ paddingBottom: '0.625em' }}>
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.validToLabel')}</FormItemName>
                    <DatePicker
                        id="batch-end-date"
                        date={validTo ? parseDate(validTo) : null}
                        onChange={(value: Date) => setValidTo(value ? format(value, 'yyyy-MM-dd') : '')}
                        status={validate && !validToDate() ? 'error' : 'normal'}
                        lowerBoundary={validFrom ? parseDate(validFrom) : null}
                    />
                    <ValidationMessage
                        className={validate && !validToDate() ? 'validation-message-visible' : ''}
                        style={{ top: '0.2em' }}
                    >
                        {t('venue.bookingCodes.reservationCodePools.generateCodes.validToError')}
                    </ValidationMessage>
                </FormItem>
                <FormItem htmlFor="batch-amount">
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.sizeLabel')}</FormItemName>
                    <NumberInput
                        id="batch-amount"
                        integerOnly
                        block
                        value={size}
                        min={1}
                        max={100000}
                        placeholder="1000"
                        status={validate && !validSize() ? 'error' : 'normal'}
                        onChange={(e) => setSize(e.target.value)}
                    />
                    <ValidationMessage className={validate && !validSize() ? 'validation-message-visible' : ''}>
                        {t('venue.bookingCodes.reservationCodePools.generateCodes.sizeError')}
                    </ValidationMessage>
                </FormItem>
                <FormItem htmlFor="batch-prefix">
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.prefixLabel')}</FormItemName>
                    <TextInput
                        id="batch-prefix"
                        type="text"
                        placeholder={t('venue.bookingCodes.reservationCodePools.generateCodes.prefixPlaceholder')}
                        maxLength={5}
                        value={prefix}
                        onChange={(e) => setPrefix(e.target.value)}
                        block
                    />
                    <ValidationMessage />
                </FormItem>
                <FormItem htmlFor="batch-note">
                    <FormItemName>{t('venue.bookingCodes.reservationCodePools.generateCodes.noteLabel')}</FormItemName>
                    <TextArea
                        id="batch-note"
                        value={note}
                        placeholder={t('venue.bookingCodes.reservationCodePools.generateCodes.notePlaceholder')}
                        onChange={(e) => setNote(e.target.value)}
                        maxLength={1000}
                        block
                    />
                    <ValidationMessage />
                </FormItem>
                <NavigationSection>
                    <ActionButton size="large" secondary onClick={onCancel}>
                        {t('common.cancel')}
                    </ActionButton>
                    <ActionButton id="generate-barcodes" size="large" onClick={onSave}>
                        {t('common.save')}
                    </ActionButton>
                </NavigationSection>
            </div>
        </ModalDialog>
    )
}
